import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { RootState } from '@/store';
import type { IThunkAPI } from '@/store/types';
import type {
  IApplicationState,
  IApplicationStatusCountList,
  IDeclineApplicationRequest,
  IDeclineReasons,
  ILockReasons,
} from '@/api/types';

import { applicationApi } from '@/api';
import { KnownErrors, ApplicationStatus, GigPhases } from '@/store/enums';
import { IApplication } from '@/api/types';

// statusId codes
export const statusIdCodes = {
  none: [0],
  new: {
    incomplete: [ApplicationStatus.IncompleteApplication],
    complete: [ApplicationStatus.ApplicationSubmitted],
    inReview: [ApplicationStatus.NewApplicantInReview],
  },
  interview: {
    invited: [ApplicationStatus.InvitetoInterview],
    scheduled: [ApplicationStatus.InterviewScheduled],
    inReview: [ApplicationStatus.InterviewInReview],
    reschedule: [ApplicationStatus.RescheduleInterview],
  },
  training: {
    invited: [ApplicationStatus.InviteToTraining],
    registered: [ApplicationStatus.RegisteredforTraining],
    reschedule: [ApplicationStatus.RescheduleClass],
  },
  application: {
    new: [ApplicationStatus.DeclinedApplication],
    interview: [ApplicationStatus.DeclinedApplication],
    training: [ApplicationStatus.DeclinedApplication],
  },
  attrition: {
    gigPhase: GigPhases.Inactive,
  },
};

interface ICountPayload {
  campaignsId: number;
  searchText?: string;
}

export const initialState: IApplicationState = {
  applicationStatusCountList: [],
  applicationList: [],
  application: null,
  applicationKeyValue: [],
  selectedApplicationId: null,
  selectedStatusIds: statusIdCodes.new.incomplete,
  isApplicationError: false,
  isApplicationSuccess: false,
  isApplicationFetching: false,
  selectedApplicationIds: [],
  selectedGigPhaseId: undefined,
  searchText: '',
};

export const getapplicationsStatusCountByCampaignsId = createAsyncThunk<
  IApplicationStatusCountList,
  ICountPayload,
  IThunkAPI
>('get/application/SU/list/status/count', async ({ campaignsId, searchText = '' }, thunkAPI) => {
  try {
    if (campaignsId > 0) {
      const response = await applicationApi.getApplicationStatusCountByCampaignsId(campaignsId, searchText);
      let data = response.data;
      if (response.data === KnownErrors.ItemNotFound) {
        data = [];
      }
      return data;
    }
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getApplicationDetailsStatusCountByCampaignId = createAsyncThunk<
  IApplication,
  { campaignsId: number },
  IThunkAPI
>('get/application/details/list', async ({ campaignsId }, thunkAPI) => {
  try {
    if (campaignsId > 0) {
      const response = await applicationApi.getApplicationDetailsStatusCountByCampaignId(campaignsId);
      let data = response.data;
      if (response.data === KnownErrors.ItemNotFound) {
        data = [];
      }
      return data;
    }
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getCampaignApplicationEndReasonList = createAsyncThunk<IDeclineReasons, number, IThunkAPI>(
  'get/pub/vocabapplicationendreason/g/list',
  async (applicationEndTypeId, thunkAPI) => {
    try {
      const response = await applicationApi.getCampaignApplicationEndReasonList(applicationEndTypeId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getCampaignApplicationLocktypesList = createAsyncThunk<ILockReasons[]>(
  'get/pub/vocabapplicationLockReason/g/list',
  async () => {
    try {
      const response = await applicationApi.getApplicationLocktypesList();
      return response.data;
    } catch (e) {
      return;
    }
  },
);

export const declineApplication = createAsyncThunk<any, IDeclineApplicationRequest, IThunkAPI>(
  'post/member/application/endreason',
  async (declineRequest, thunkAPI) => {
    try {
      const response = await applicationApi.declineApplication(declineRequest);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    clearapplicationss(state) {
      state.applicationList = [];
    },
    selectApplicationId(state, { payload }) {
      state.selectedApplicationId = payload;
    },
    selectStatusIdList(state, { payload }) {
      state.selectedStatusIds = payload;
      state.selectedGigPhaseId = undefined;
    },
    selectApplicationIds(state, { payload }) {
      state.selectedApplicationIds = payload;
    },
    selectGigPhaseId(state, { payload }) {
      state.selectedGigPhaseId = payload;
      state.selectedStatusIds = statusIdCodes.none;
    },
    setSearchText(state, { payload }) {
      state.searchText = payload;
    },
    reSetSearch(state, { payload }) {
      state.searchText = payload?.searchText || '';
      state.selectedStatusIds = payload?.selectedStatusId || statusIdCodes.new.complete;
    },
    setStatusLabel(state, { payload }) {
      state.statusLabel = payload?.statusLabel || '';
    },
  },
  extraReducers: builder => {
    builder

      .addCase(getapplicationsStatusCountByCampaignsId.fulfilled, (state, action) => {
        if (Array.isArray(action.payload) && action.payload.length > 0) {
          state.applicationStatusCountList = action?.payload;
        } else {
          state.applicationStatusCountList = [];
        }
      })
      .addCase(getApplicationDetailsStatusCountByCampaignId.fulfilled, (state, action) => {
        if (Array.isArray(action.payload) && action.payload.length > 0) {
          state.application = action?.payload;
        } else {
          state.application = null;
        }
      });
  },
});

// actions
export const {
  clearapplicationss,
  selectApplicationId,
  selectStatusIdList,
  selectGigPhaseId,
  selectApplicationIds,
  setSearchText,
  reSetSearch,
  setStatusLabel,
} = applicationSlice.actions;

// selectors

export const applicationList = (state: RootState): IApplicationState['applicationList'] =>
  state.application.applicationList;
export const application = (state: RootState): IApplicationState['application'] => state.application.application;
export const isApplicationFetching = (state: RootState): boolean => state.application.isApplicationFetching;
export default applicationSlice.reducer;
